/* =========================================================================
   PackPro — Global behavior module
   - Scroll-triggered reveal (Intersection Observer)
   - Sticky header glass-on-scroll
   - Back-to-top button
   - Cookie consent banner (localStorage persisted)
   - Animated counters
   - Smooth scroll for hash anchors
   ========================================================================= */
(function () {
  'use strict';

  const root = document.documentElement;
  const body = document.body;
  const STORAGE_COOKIE = 'pp-cookie-ack';

  /* ---------- 1. Scroll-triggered reveal -------------------------------- */
  function initReveal() {
    const targets = document.querySelectorAll('.pp-reveal');
    if (!targets.length) return;

    if (!('IntersectionObserver' in window)) {
      targets.forEach((el) => el.classList.add('is-visible'));
      return;
    }

    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('is-visible');
            io.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.12, rootMargin: '0px 0px -40px 0px' }
    );

    targets.forEach((el) => io.observe(el));
  }

  /* ---------- 2. Header glass on scroll + back-to-top visibility -------- */
  function initScrollFx() {
    const toTop = document.querySelector('.pp-to-top');
    let ticking = false;
    const onScroll = () => {
      const y = window.scrollY || document.documentElement.scrollTop;
      body.classList.toggle('pp-scrolled', y > 30);
      if (toTop) toTop.classList.toggle('is-visible', y > 300);
      ticking = false;
    };
    window.addEventListener(
      'scroll',
      () => {
        if (!ticking) { requestAnimationFrame(onScroll); ticking = true; }
      },
      { passive: true }
    );
    onScroll();
  }

  /* ---------- 3. Back-to-top click -------------------------------------- */
  function initBackToTop() {
    const btn = document.querySelector('.pp-to-top');
    if (!btn) return;
    btn.addEventListener('click', () => {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    });
  }

  /* ---------- 4. Cookie consent ----------------------------------------- */
  function initCookie() {
    const banner = document.querySelector('.pp-cookie');
    if (!banner) return;
    let acked = false;
    try { acked = localStorage.getItem(STORAGE_COOKIE) === '1'; } catch (_) {}
    if (acked) { banner.remove(); return; }

    setTimeout(() => banner.classList.add('is-visible'), 800);

    banner.querySelectorAll('[data-pp-cookie-action]').forEach((btn) => {
      btn.addEventListener('click', () => {
        try { localStorage.setItem(STORAGE_COOKIE, '1'); } catch (_) {}
        banner.classList.remove('is-visible');
        setTimeout(() => banner.remove(), 600);
      });
    });
  }

  /* ---------- 5. Animated counters -------------------------------------- */
  function animateCounter(el) {
    const target = parseFloat(el.dataset.ppCounter || '0');
    const duration = parseInt(el.dataset.ppDuration || '1600', 10);
    const decimals = parseInt(el.dataset.ppDecimals || '0', 10);
    const start = performance.now();
    const ease = (t) => 1 - Math.pow(1 - t, 3);
    function tick(now) {
      const p = Math.min(1, (now - start) / duration);
      const v = target * ease(p);
      el.textContent = decimals ? v.toFixed(decimals) : Math.round(v).toLocaleString();
      if (p < 1) requestAnimationFrame(tick);
    }
    requestAnimationFrame(tick);
  }
  function initCounters() {
    const counters = document.querySelectorAll('[data-pp-counter]');
    if (!counters.length) return;
    if (!('IntersectionObserver' in window)) {
      counters.forEach(animateCounter);
      return;
    }
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) {
            animateCounter(e.target);
            io.unobserve(e.target);
          }
        });
      },
      { threshold: 0.4 }
    );
    counters.forEach((el) => io.observe(el));
  }

  /** Resolve hash target by element id. */
  function resolveHashTarget(hash) {
    if (!hash || hash === '#') return null;
    const id = decodeURIComponent(hash.slice(1));
    let el = document.getElementById(id);
    if (el) return el;
    return null;
  }

  function scrollToHashTarget(hash) {
    const target = resolveHashTarget(hash);
    if (!target) return false;
    const offset = parseFloat(getComputedStyle(body).getPropertyValue('--header-height')) || 80;
    window.scrollTo({
      top: target.getBoundingClientRect().top + window.scrollY - offset - 12,
      behavior: 'smooth',
    });
    return true;
  }

  /* ---------- PackPro product cards — qty + money (collection + recommendations) */
  const PP_DEFAULT_DECIMALS = 2;
  const PP_CURRENCY_DECIMALS = {
    BHD: 3,
    BIF: 0,
    BYR: 0,
    CLF: 4,
    CLP: 0,
    DJF: 0,
    GNF: 0,
    IQD: 3,
    ISK: 0,
    JOD: 3,
    JPY: 0,
    KMF: 0,
    KRW: 0,
    KWD: 3,
    LYD: 3,
    MRO: 5,
    OMR: 3,
    PYG: 0,
    RWF: 0,
    TND: 3,
    UGX: 0,
    UYI: 0,
    UYW: 4,
    VND: 0,
    VUV: 0,
    XAF: 0,
    XAG: 0,
    XAU: 0,
    XBA: 0,
    XBB: 0,
    XBC: 0,
    XBD: 0,
    XDR: 0,
    XOF: 0,
    XPD: 0,
    XPF: 0,
    XPT: 0,
    XSU: 0,
    XTS: 0,
    XUA: 0,
  };

  /**
   * Shopify storefront `variant.price` is not always scaled by 10^ISO exponent.
   * For some ISO 4217 three-decimal currencies (ex. TND), Liquid `| money` shows three
   * fractional digits while the integer price uses cent-like scaling (÷100), not ÷1000.
   * Divisor here converts integer minor units → major units for arithmetic only.
   */
  const SHOPIFY_INTEGER_MINOR_DIVISOR = {
    TND: 100,
  };

  function minorUnitsToMajorDivisor(currencyCode) {
    const c = String(currencyCode || '')
      .trim()
      .toUpperCase();
    if (Object.prototype.hasOwnProperty.call(SHOPIFY_INTEGER_MINOR_DIVISOR, c)) {
      return SHOPIFY_INTEGER_MINOR_DIVISOR[c];
    }
    const exp = PP_CURRENCY_DECIMALS[c] ?? PP_DEFAULT_DECIMALS;
    return Math.pow(10, exp);
  }

  function ppFormatCents(moneyValue, thousandsSeparator, decimalSeparator, precision, divisor) {
    const roundedNumber = (moneyValue / divisor).toFixed(precision);
    const parts = roundedNumber.split('.');
    let a = parts[0] || '0';
    const b = parts[1] || '';
    a = a.replace(/\d(?=(\d\d\d)+(?!\d))/g, (digit) => digit + thousandsSeparator);
    return precision <= 0 ? a : a + decimalSeparator + b.padEnd(precision, '0');
  }

  function ppFormatMoney(minorUnits, formatTemplate, currencyCode) {
    if (!formatTemplate || currencyCode == null) return '';
    const cur = String(currencyCode).trim().toUpperCase();
    const displayPrecision = PP_CURRENCY_DECIMALS[cur] ?? PP_DEFAULT_DECIMALS;
    const divisor = minorUnitsToMajorDivisor(cur);

    return formatTemplate.replace(/{{\s*(\w+)\s*}}/g, (_, placeholder) => {
      if (typeof placeholder !== 'string') return '';
      if (placeholder === 'currency') return cur;

      let thousandsSeparator = ',';
      let decimalSeparator = '.';
      let precision = displayPrecision;

      switch (placeholder) {
        case 'amount':
          break;
        case 'amount_no_decimals':
          precision = 0;
          break;
        case 'amount_with_comma_separator':
          thousandsSeparator = '.';
          decimalSeparator = ',';
          break;
        case 'amount_no_decimals_with_comma_separator':
          thousandsSeparator = '.';
          precision = 0;
          break;
        case 'amount_no_decimals_with_space_separator':
          thousandsSeparator = ' ';
          precision = 0;
          break;
        case 'amount_with_space_separator':
          thousandsSeparator = ' ';
          decimalSeparator = ',';
          break;
        case 'amount_with_period_and_space_separator':
          thousandsSeparator = ' ';
          decimalSeparator = '.';
          break;
        case 'amount_with_apostrophe_separator':
          thousandsSeparator = "'";
          decimalSeparator = '.';
          break;
        default:
          break;
      }

      return ppFormatCents(minorUnits, thousandsSeparator, decimalSeparator, precision, divisor);
    });
  }

  function resolvePackproMoneyConfig(fromEl) {
    const idCfg = document.getElementById('packpro-money-config');
    if (idCfg) {
      return {
        format: idCfg.getAttribute('data-format'),
        currency: idCfg.getAttribute('data-currency'),
      };
    }
    const host = fromEl.closest('[data-packpro-money-format]');
    if (host) {
      return {
        format: host.getAttribute('data-packpro-money-format'),
        currency: host.getAttribute('data-packpro-currency'),
      };
    }
    return { format: null, currency: null };
  }

  const PP_PACKPRO_QTY_ROOT =
    '.product-grid.product-grid--packpro, .resource-list.resource-list--packpro, .pp-feat__viewport';

  const PP_STOCK_OUT = 'Rupture de stock';
  const PP_STOCK_LOW = 'Stock faible';
  const PP_STOCK_IN = 'En stock';
  const PP_ADD_TO_CART = 'Ajouter au panier';

  function refreshPackproCardUnitPriceDisplay(card) {
    const qty = card && card.querySelector('[data-pp-qty]');
    if (!qty) return;
    applyPackproQtyStepper(qty, null, qty.querySelector('.pp-qty__input')?.value);
  }

  function applyPackproVariantChange(selectEl) {
    const card = selectEl.closest('.pp-prod');
    if (!card) return;

    const opt = selectEl.selectedOptions && selectEl.selectedOptions[0];
    if (!opt) return;

    const cents = parseInt(opt.dataset.ppPriceCents || '', 10);
    const compareCents = parseInt(opt.dataset.ppCompareCents || '0', 10);
    const available = opt.dataset.ppAvailable === 'true';
    const lowStock = opt.dataset.ppLowStock === 'true';
    const imgUrl = opt.dataset.ppImg || '';
    const variantId = opt.value;

    const priceEl = card.querySelector('.pp-prod__price');
    const compareEl = card.querySelector('[data-pp-compare]');
    const badge = card.querySelector('.pp-prod__badge--sale');
    const imgEl = card.querySelector('.pp-prod__img');
    const stockWrap = card.querySelector('.pp-prod__stock');
    const stockLabel = card.querySelector('.pp-prod__stock-label');
    const btn = card.querySelector('.pp-prod__cta');
    const mediaLink = card.querySelector('.pp-prod__media');
    const titleLink = card.querySelector('.pp-prod__title');
    const baseUrl = card.dataset.productUrl || '';

    if (priceEl && !Number.isNaN(cents)) {
      priceEl.dataset.ppUnitCents = String(cents);
    }

    const onSale = !Number.isNaN(cents) && compareCents > cents;
    if (badge) {
      badge.hidden = !onSale;
    }

    if (compareEl) {
      const cfg = resolvePackproMoneyConfig(card);
      if (onSale && cfg.format && cfg.currency && !Number.isNaN(compareCents)) {
        compareEl.hidden = false;
        compareEl.textContent = ppFormatMoney(compareCents, cfg.format, cfg.currency);
      } else {
        compareEl.hidden = true;
        compareEl.textContent = '';
      }
    }

    if (imgEl && imgUrl) {
      imgEl.src = imgUrl;
    }

    if (stockWrap) {
      stockWrap.classList.toggle('pp-prod__stock--in', available);
      stockWrap.classList.toggle('pp-prod__stock--out', !available);
    }
    if (stockLabel) {
      if (!available) stockLabel.textContent = PP_STOCK_OUT;
      else if (lowStock) stockLabel.textContent = PP_STOCK_LOW;
      else stockLabel.textContent = PP_STOCK_IN;
    }

    if (btn) {
      btn.disabled = !available;
      const label = available ? PP_ADD_TO_CART : PP_STOCK_OUT;
      let textFound = false;
      for (let i = btn.childNodes.length - 1; i >= 0; i--) {
        const n = btn.childNodes[i];
        if (n.nodeType === Node.TEXT_NODE) {
          n.textContent = label;
          textFound = true;
          break;
        }
      }
      if (!textFound) btn.appendChild(document.createTextNode(label));
    }

    const qp = baseUrl && variantId ? `${baseUrl.split('?')[0]}?variant=${variantId}` : '';
    if (qp && mediaLink) mediaLink.setAttribute('href', qp);
    if (qp && titleLink) titleLink.setAttribute('href', qp);

    refreshPackproCardUnitPriceDisplay(card);
  }

  function initPackproVariantSelectDelegation() {
    if (document.documentElement.dataset.ppPackproVariantDelegation === '1') return;
    document.documentElement.dataset.ppPackproVariantDelegation = '1';

    document.addEventListener('change', (e) => {
      const t = e.target;
      if (!(t instanceof HTMLSelectElement)) return;
      if (!t.hasAttribute('data-pp-variant-select')) return;
      applyPackproVariantChange(t);
    });
  }

  function applyPackproQtyStepper(qty, delta, rawInput) {
    const input = qty.querySelector('.pp-qty__input');
    if (!input) return;

    const card = qty.closest('.pp-prod');
    const price = card && card.querySelector('.pp-prod__price');
    const mirror = card && card.querySelector('[data-pp-qty-mirror]');
    const unitCentsRaw = price && price.dataset.ppUnitCents;
    const unitCents = unitCentsRaw != null ? parseInt(unitCentsRaw, 10) : NaN;
    const cfg = resolvePackproMoneyConfig(qty);

    let n = parseInt(input.value, 10) || 1;
    if (rawInput !== undefined && rawInput !== null && String(rawInput).trim() !== '') {
      const parsed = parseInt(String(rawInput), 10);
      n = Number.isNaN(parsed) ? 1 : parsed;
    } else if (delta != null && !Number.isNaN(delta)) {
      n = n + delta;
    }
    if (Number.isNaN(n) || n < 1) n = 1;

    input.value = String(n);
    if (mirror) mirror.value = String(n);

    if (price && cfg.format && cfg.currency && unitCentsRaw != null && !Number.isNaN(unitCents)) {
      price.textContent = ppFormatMoney(unitCents * n, cfg.format, cfg.currency);
    }
  }

  function initPackproPackproQtyDelegation() {
    if (document.documentElement.dataset.ppPackproQtyDelegation === '1') return;
    document.documentElement.dataset.ppPackproQtyDelegation = '1';

    document.addEventListener('click', (e) => {
      const btn = e.target.closest('[data-pp-step]');
      if (!btn) return;
      const qty = btn.closest('[data-pp-qty]');
      if (!qty || !qty.closest(PP_PACKPRO_QTY_ROOT)) return;
      const d = parseInt(btn.getAttribute('data-pp-step'), 10);
      if (Number.isNaN(d)) return;
      e.preventDefault();
      applyPackproQtyStepper(qty, d, null);
    });

    document.addEventListener('input', (e) => {
      const t = e.target;
      if (!(t instanceof HTMLInputElement)) return;
      if (!t.classList.contains('pp-qty__input')) return;
      const qty = t.closest('[data-pp-qty]');
      if (!qty || !qty.closest(PP_PACKPRO_QTY_ROOT)) return;
      applyPackproQtyStepper(qty, null, t.value);
    });

    document.addEventListener('change', (e) => {
      const t = e.target;
      if (!(t instanceof HTMLInputElement)) return;
      if (!t.classList.contains('pp-qty__input')) return;
      const qty = t.closest('[data-pp-qty]');
      if (!qty || !qty.closest(PP_PACKPRO_QTY_ROOT)) return;
      applyPackproQtyStepper(qty, null, t.value);
    });
  }

  function collectPackproCartSectionIds() {
    /** @type {string[]} */
    const ids = [];
    document.querySelectorAll('cart-items-component[data-section-id]').forEach((el) => {
      const sid = el.getAttribute('data-section-id');
      if (sid) ids.push(sid);
    });
    return ids;
  }

  function packproCardIsOutOfStock(card) {
    const stock = card.querySelector('.pp-prod__stock');
    return !!(stock && stock.classList.contains('pp-prod__stock--out'));
  }

  function initPackproAjaxAddToCart() {
    if (document.documentElement.dataset.ppPackproAjaxCart === '1') return;
    document.documentElement.dataset.ppPackproAjaxCart = '1';

    document.addEventListener('submit', async (e) => {
      const form = e.target;
      if (!(form instanceof HTMLFormElement)) return;
      if (!form.classList.contains('pp-prod__form')) return;

      const card = form.closest('.pp-prod');
      if (!card || !card.closest(PP_PACKPRO_QTY_ROOT)) return;

      e.preventDefault();

      const cartAddUrl = typeof Theme !== 'undefined' && Theme.routes ? Theme.routes.cart_add_url : '';
      if (!cartAddUrl) return;

      const btn = form.querySelector('button[type="submit"].pp-prod__cta');
      if (btn) btn.disabled = true;

      const formData = new FormData(form);
      const sectionIds = collectPackproCartSectionIds();
      if (sectionIds.length) formData.append('sections', sectionIds.join(','));

      try {
        const response = await fetch(cartAddUrl, {
          method: 'POST',
          headers: {
            'X-Requested-With': 'XMLHttpRequest',
            Accept: 'application/json',
          },
          body: formData,
        });

        const data = await response.json();

        if (data.status) {
          window.alert(data.message || data.description || 'Impossible d’ajouter au panier.');
          return;
        }

        const cartRes = await fetch('/cart.js');
        const cart = await cartRes.json();

        document.dispatchEvent(
          new CustomEvent('cart:update', {
            bubbles: true,
            detail: {
              resource: cart,
              sourceId: form.id,
              data: {
                source: 'packpro-product-card',
                itemCount: cart.item_count,
                productId: card.dataset.productId,
                sections: data.sections,
              },
            },
          })
        );

        const drawer = document.querySelector('cart-drawer-component');
        if (drawer && typeof drawer.open === 'function') drawer.open();
      } catch (err) {
        console.error(err);
        window.alert('Une erreur est survenue. Réessayez.');
      } finally {
        if (btn && !packproCardIsOutOfStock(card)) btn.disabled = false;
      }
    });
  }

  /* ---------- 6. Smooth-scroll for hash links --------------------------- */
  function initSmoothScroll() {
    function tryScrollInitialHash() {
      if (!window.location.hash) return;
      scrollToHashTarget(window.location.hash);
    }

    document.addEventListener('click', (e) => {
      const link = e.target.closest('a[href*="#"]');
      if (!link) return;

      const raw = link.getAttribute('href');
      if (!raw || /^mailto:|^tel:|^javascript:/i.test(raw.trim())) return;

      let parsed;
      try {
        parsed = new URL(link.href, window.location.href);
      } catch (_) {
        return;
      }

      const hash = parsed.hash;
      if (!hash || hash === '#') return;

      const here = `${window.location.pathname}${window.location.search}`;
      const there = `${parsed.pathname}${parsed.search}`;
      const norm = (path) => (path.replace(/\/$/, '') || '/');
      if (norm(here.split('?')[0]) !== norm(there.split('?')[0])) return;

      if (!resolveHashTarget(hash)) return;

      e.preventDefault();
      scrollToHashTarget(hash);
    });

    window.addEventListener('hashchange', tryScrollInitialHash);
    requestAnimationFrame(tryScrollInitialHash);
    window.addEventListener('load', tryScrollInitialHash);

    document.addEventListener('shopify:section:load', () =>
      requestAnimationFrame(tryScrollInitialHash)
    );
  }

  /* ---------- Bootstrap ------------------------------------------------- */
  function boot() {
    const inThemeEditor = root.classList.contains('shopify-design-mode');

    initReveal();
    initScrollFx();
    initCounters();

    initPackproVariantSelectDelegation();
    initPackproPackproQtyDelegation();
    initPackproAjaxAddToCart();

    if (!inThemeEditor) {
      initBackToTop();
      initCookie();
      initSmoothScroll();
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  document.addEventListener('shopify:section:load', () => {
    initReveal();
    initCounters();
  });

  window.PackPro = Object.assign(window.PackPro || {}, {
    initReveal,
    initCounters,
    animateCounter,
  });
})();
